/** Color Balance adjustment — pure math + types, no React, no Fabric.
 *
 *  Three independent shifts applied per tonal range
 *  (shadows / midtones / highlights), each carrying three axes:
 *  cyan↔red, magenta↔green, yellow↔blue. Tonal weighting uses a
 *  triangular falloff: shadows peaks at L=0, midtones at L=0.5,
 *  highlights at L=1; the three weights sum to 1 at every L so the
 *  total shift is bounded by the user's slider range.
 *
 *  Optional Preserve Luminosity rescales the post-shift RGB so its
 *  luminance matches the source. Without it, e.g. shifting toward
 *  red also brightens the pixel. With it on (Photoshop's default),
 *  only the chroma shifts. */

export type ColorBalanceTriple = [number, number, number];

export interface ColorBalanceValue {
  /** [cyan-red, magenta-green, yellow-blue] for shadows. -100..+100 each. */
  shadows: ColorBalanceTriple;
  /** Same shape as shadows, applied weighted to midtones. */
  midtones: ColorBalanceTriple;
  /** Same shape as shadows, applied weighted to highlights. */
  highlights: ColorBalanceTriple;
  /** Photoshop's "Preserve Luminosity" toggle. When true, the
   *  post-shift RGB is rescaled to match the source pixel's
   *  luminance — only chroma shifts, no perceived brightness change. */
  preserveLuminosity: boolean;
}

export const identityColorBalanceValue = (): ColorBalanceValue => ({
  shadows: [0, 0, 0],
  midtones: [0, 0, 0],
  highlights: [0, 0, 0],
  preserveLuminosity: true,
});

import { LUMA_B, LUMA_G, LUMA_R } from '../color-constants';

/** Maps slider units (-100..+100) to a per-pixel additive shift in
 *  the [-1, +1] normalised RGB space. Tuned visually so ±100 on a
 *  single axis is a clearly visible cast without instantly clipping.
 *
 *  Exported so the GPU shader can interpolate it into shader source
 *  at compile time and stay byte-equivalent with the JS path — see
 *  `lib/gpu/adjustments/color-balance.ts`. */
export const COLOR_BALANCE_SHIFT_SCALE = 0.005;

/** Pure pixel transform — returns a fresh ImageData. Tests use
 *  this variant; the canvas-bound wrapper does the putImageData. */
export function applyColorBalancePixels(source: ImageData, value: ColorBalanceValue): ImageData {
  const w = source.width;
  const h = source.height;
  const sd = source.data;
  const out = new Uint8ClampedArray(sd.length);
  const sCR = value.shadows[0];
  const sMG = value.shadows[1];
  const sYB = value.shadows[2];
  const mCR = value.midtones[0];
  const mMG = value.midtones[1];
  const mYB = value.midtones[2];
  const hCR = value.highlights[0];
  const hMG = value.highlights[1];
  const hYB = value.highlights[2];
  const preserve = value.preserveLuminosity;

  for (let i = 0; i < sd.length; i += 4) {
    const r0 = sd[i] / 255;
    const g0 = sd[i + 1] / 255;
    const b0 = sd[i + 2] / 255;

    const L = LUMA_R * r0 + LUMA_G * g0 + LUMA_B * b0;

    // Triangular tonal weights summing to 1 at every L. Shadows
    // peaks at L=0, midtones at L=0.5, highlights at L=1.
    let wS: number;
    let wM: number;
    let wH: number;
    if (L < 0.5) {
      wS = 1 - 2 * L;
      wM = 2 * L;
      wH = 0;
    } else {
      wS = 0;
      wM = 2 - 2 * L;
      wH = 2 * L - 1;
    }

    const dR = (sCR * wS + mCR * wM + hCR * wH) * COLOR_BALANCE_SHIFT_SCALE;
    const dG = (sMG * wS + mMG * wM + hMG * wH) * COLOR_BALANCE_SHIFT_SCALE;
    const dB = (sYB * wS + mYB * wM + hYB * wH) * COLOR_BALANCE_SHIFT_SCALE;

    let nr = r0 + dR;
    let ng = g0 + dG;
    let nb = b0 + dB;

    if (preserve) {
      // Rescale post-shift RGB to restore the source luminance.
      // Skip when post-shift L is ~0 to avoid divide-by-zero.
      const Lnew = LUMA_R * nr + LUMA_G * ng + LUMA_B * nb;
      if (Lnew > 1e-6) {
        const scale = L / Lnew;
        nr *= scale;
        ng *= scale;
        nb *= scale;
      }
    }

    if (nr < 0) nr = 0;
    else if (nr > 1) nr = 1;
    if (ng < 0) ng = 0;
    else if (ng > 1) ng = 1;
    if (nb < 0) nb = 0;
    else if (nb > 1) nb = 1;

    out[i] = (nr * 255 + 0.5) | 0;
    out[i + 1] = (ng * 255 + 0.5) | 0;
    out[i + 2] = (nb * 255 + 0.5) | 0;
    out[i + 3] = sd[i + 3];
  }
  return new ImageData(out, w, h);
}

export function applyColorBalanceToCanvas(
  source: ImageData,
  target: HTMLCanvasElement,
  value: ColorBalanceValue,
): void {
  const tCtx = target.getContext('2d');
  if (!tCtx) return;
  if (target.width !== source.width || target.height !== source.height) {
    target.width = source.width;
    target.height = source.height;
  }
  tCtx.putImageData(applyColorBalancePixels(source, value), 0, 0);
}
